import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { sfx } from "@/lib/sfx";
import { useToast } from "@/contexts/ToastContext";

interface Invite {
  id: string; code: string; used: boolean;
  used_at: string | null; created_at: string;
  expires_at: string | null;
}
interface Payload {
  invites: Invite[];
  counts: { total: number; used: number; pending: number };
}

export function InvitesTab() {
  const [data, setData] = useState<Payload | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const toast = useToast();

  const refresh = useCallback(async () => {
    try { setData(await api<Payload>("/api/invites")); }
    catch (e) { setErr(e instanceof Error ? e.message : "Erreur"); }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);

  const create = async () => {
    setBusy(true); setErr(null);
    try {
      const created = await api<{ code: string }>("/api/invites", { method: "POST", body: "{}" });
      sfx.chirp();
      toast.show(`Code créé : ${created.code}`);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally { setBusy(false); }
  };

  const revoke = async (id: string) => {
    sfx.tap();
    try {
      await api(`/api/invites/${id}`, { method: "DELETE" });
      await refresh();
    } catch (e) { setErr(e instanceof Error ? e.message : "Erreur"); }
  };

  const copy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      sfx.pop();
      toast.show("Code copié ✓");
    } catch { toast.show("Copie impossible — sélectionne et Ctrl+C"); }
  };

  if (!data) return <div className="muted">Chargement…</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div className="muted" style={{ fontSize: 13 }}>
          {data.counts.total} code{data.counts.total > 1 ? "s" : ""} — {data.counts.used} utilisé{data.counts.used > 1 ? "s" : ""}
        </div>
        <button className="btn primary" onClick={() => void create()} disabled={busy}>
          {busy ? "…" : "+ Nouveau code"}
        </button>
      </div>

      <div style={{ maxHeight: "42vh", overflowY: "auto", display: "flex",
                    flexDirection: "column", gap: 6 }}>
        {data.invites.length === 0 && (
          <div className="muted" style={{ padding: 12 }}>
            Aucun code pour l'instant. Clique + Nouveau code et colle-le à un pote.
          </div>
        )}
        {data.invites.map((i) => (
          <div key={i.id} style={{
            display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
            background: "var(--bg-elev-2)", borderRadius: 8,
          }}>
            <code style={{
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
              fontSize: 15, fontWeight: 700, letterSpacing: 1,
              color: i.used ? "var(--fg-mute)" : "var(--accent)",
              textDecoration: i.used ? "line-through" : "none",
            }}>{i.code}</code>
            <div style={{ flex: 1, fontSize: 12, color: "var(--fg-mute)" }}>
              {i.used ? `activé le ${new Date(i.used_at!).toLocaleDateString("fr-FR")}` :
                        `créé le ${new Date(i.created_at).toLocaleDateString("fr-FR")}`}
            </div>
            {!i.used && (
              <>
                <button className="btn" onClick={() => void copy(i.code)}
                        style={{ padding: "4px 10px", fontSize: 12 }}>Copier</button>
                <button className="btn" onClick={() => void revoke(i.id)}
                        style={{ padding: "4px 10px", fontSize: 12,
                                 color: "#f87171", borderColor: "#7a2f2f" }}>Révoquer</button>
              </>
            )}
          </div>
        ))}
      </div>

      {err && <div style={{ color: "#f87171", fontSize: 13 }}>{err}</div>}
    </div>
  );
}
